import { RightOutlined } from '@ant-design/icons';
import { Button, Collapse, type CollapseProps } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import './CollapsedFields.scss';

import { SpecificationField } from 'src/components/base/fields/SpecificationField/SpecificationField';
import { TextInputField } from 'src/components/base/fields/TextInputField';
import { getDependentFieldsChain } from 'src/helpers/getDependentFieldsChain';
import { scrollToElementById } from 'src/helpers/scrollToElementById';
import { useDebounceEffect } from 'src/hooks/useDebounceEffect';
import type { ICustomerLeadProductSpecificationData } from 'src/types/customers/ICustomerLeadProductSpecificationData';
import type {
  IProductSpecificationFieldReference,
  IProductSpecificationReference,
} from 'src/types/reference/IProductSpecificationReference';
import { ProductSpecificationFieldGroup } from 'src/types/reference/ProductSpecificationFieldGroup';

type CollapsedFieldsPanelFooterProps = {
  isFirst?: boolean;
  isLast?: boolean;

  isDisabled?: boolean;

  onPrevious?: () => void;
  onNext?: () => void;
};

function CollapsedFieldsPanelFooter({
  isFirst,
  isLast,
  isDisabled,
  onPrevious,
  onNext,
}: Readonly<CollapsedFieldsPanelFooterProps>) {
  return (
    <div className="collapsed-fields__panel-footer">
      {!isFirst && (
        <Button type="default" disabled={isDisabled} loading={isDisabled} onClick={onPrevious}>
          Previous
        </Button>
      )}
      {!isLast && (
        <Button
          className="collapsed-fields__panel-footer__next-button"
          type="primary"
          ghost={true}
          disabled={isDisabled}
          icon={<RightOutlined />}
          loading={isDisabled}
          onClick={onNext}
        >
          Next
        </Button>
      )}
    </div>
  );
}

type CollapsedFieldsProps = {
  fieldGroup: ProductSpecificationFieldGroup;
  fieldsForGroup: IProductSpecificationFieldReference[];
  specification: IProductSpecificationReference;
  specData: ICustomerLeadProductSpecificationData;
};

export function CollapsedFields({
  fieldGroup,
  fieldsForGroup,
  specification,
  specData,
}: Readonly<CollapsedFieldsProps>) {
  const showInitialQty = fieldGroup === ProductSpecificationFieldGroup.Quantities;

  // Select fields which do not depends on other fields
  const rootFields = useMemo(() => fieldsForGroup.filter((field) => !field.dependsOnId), [fieldsForGroup]);

  const allKeys = useMemo(
    () => [...(showInitialQty ? ['initialQty'] : []), ...rootFields.map((field) => field.id)],
    [showInitialQty, rootFields]
  );

  const [activeKey, setActiveKey] = useState<string | undefined>(allKeys?.[0]);

  const handlePreviousClick = useCallback(() => {
    const newIndex = activeKey ? allKeys.indexOf(activeKey) - 1 : 0;

    if (newIndex >= 0 && newIndex < allKeys.length) {
      setActiveKey(allKeys?.[newIndex]);
    }
  }, [activeKey, allKeys]);

  const handleNextClick = useCallback(() => {
    const newIndex = activeKey ? allKeys.indexOf(activeKey) + 1 : -1;

    if (newIndex >= 0 && newIndex < allKeys.length) {
      setActiveKey(allKeys?.[newIndex]);
    }
  }, [activeKey, allKeys]);

  const collapseItems: Exclude<CollapseProps['items'], undefined> = useMemo(() => {
    let counter = 1;

    return [
      ...(showInitialQty
        ? [
            {
              key: 'initialQty',
              label: `${counter++}. Initial Quantity`,
              forceRender: true,
              children: (
                <div className="collapsed-fields__panel" id="initialQty">
                  <TextInputField type="number" name="initialQty" showControlIcons={true} />
                  <CollapsedFieldsPanelFooter
                    isFirst={true}
                    onPrevious={handlePreviousClick}
                    onNext={handleNextClick}
                  />
                </div>
              ),
            },
          ]
        : []),
      ...rootFields.map(
        (field, fieldIndex) =>
          ({
            key: field.id,
            label: `${counter++}. ${field.name}`,
            forceRender: true,
            children: (
              <div className="collapsed-fields__panel" id={field.id}>
                {getDependentFieldsChain(field, fieldsForGroup).map((fieldsChainItem, index) => (
                  <SpecificationField
                    key={fieldsChainItem.id}
                    field={fieldsChainItem}
                    specification={specification}
                    specificationData={specData}
                    hideLabel={index === 0}
                  />
                ))}
                <CollapsedFieldsPanelFooter
                  isFirst={!showInitialQty && fieldIndex === 0}
                  isLast={fieldIndex === rootFields.length - 1}
                  onPrevious={handlePreviousClick}
                  onNext={handleNextClick}
                />
              </div>
            ),
          }) as Exclude<CollapseProps['items'], undefined>[0]
      ),
    ];
  }, [showInitialQty, rootFields, handlePreviousClick, handleNextClick, fieldsForGroup, specification, specData]);

  const onCollapsePanelChange = useCallback((key: string | string[]) => {
    setActiveKey(Array.isArray(key) ? key[0] : key);
  }, []);

  // Need some time to wait when panel animation ends. 200ms by default
  useDebounceEffect(() => {
    if (activeKey) {
      scrollToElementById(activeKey);
    }
  }, [activeKey]);

  return (
    <Collapse
      className="collapsed-fields__container"
      accordion={true}
      activeKey={activeKey}
      items={collapseItems}
      onChange={onCollapsePanelChange}
    />
  );
}
